/**
 * Network Graph Component
 *
 * Displays hierarchical data as an interactive network graph
 * Using React Flow for visualization
 * Updated: Horizontal left-to-right layout, improved readability
 * Version: 3.1 - By Release Type support
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './NetworkGraph.css';
import ProductTypeIcon from '../ProductTypeIcon';

const NetworkGraph = ({ hierarchyData, onNodeClick, expandAllTrigger }) => {
  const history = useHistory();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [expandedNodes, setExpandedNodes] = useState(() => {
    // Initialize with only the first SKI Release expanded (not products or request types)
    const initialExpanded = new Set();
    if (hierarchyData && hierarchyData.length > 0) {
      const firstItem = hierarchyData[0];
      // Auto-expand first SKI release, or packages_group (so packages are visible on load)
      if (firstItem.type === 'ski_release' || firstItem.type === 'package' || firstItem.type === 'packages_group') {
        initialExpanded.add(firstItem.id);
      }
    }
    return initialExpanded;
  });
  const [multiRequestModal, setMultiRequestModal] = useState({
    show: false,
    productName: '',
    requestIds: []
  });

  // Expand all non-leaf nodes when expandAllTrigger increments
  useEffect(() => {
    if (!expandAllTrigger) return;
    const allIds = new Set();
    const collectIds = (nodeList) => {
      nodeList.forEach(node => {
        if (node.children && node.children.length > 0) {
          allIds.add(node.id);
          collectIds(node.children);
        }
      });
    };
    collectIds(hierarchyData);
    setExpandedNodes(allIds);
  }, [expandAllTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Check if a request type name is CuC
   */
  const isCuCRequestType = (requestTypeName) => {
    if (!requestTypeName) return false;
    return requestTypeName.toLowerCase().includes('capability update catalog') ||
           requestTypeName === 'Capability Update Catalog';
  };

  /**
   * Extract request type name from node ID
   */
  const extractRequestTypeFromId = (nodeId, nodeData) => {
    if (!nodeId) return null;

    if (nodeId.includes('capability-update-catalog')) {
      return 'Capability Update Catalog';
    }

    if (nodeId.includes('guidewire-production-readiness') || nodeId.includes('gpr')) {
      return 'Guidewire Production Readiness';
    }

    if (nodeId.includes('salesops-request')) {
      return 'SalesOps Request';
    }

    if (nodeId.includes('pricing-packaging') || nodeId.includes('pricing-and-packaging') || nodeId.includes('pricing-&-packaging')) {
      return 'Pricing & Packaging';
    }

    if (nodeData.name) {
      return nodeData.name;
    }

    return null;
  };

  /**
   * Transform hierarchical data into React Flow nodes and edges
   * Layout: Left to Right (Shell -> SKI Release -> [Release Type ->] Request Types -> Products)
   */
  const transformDataToGraph = useCallback((data, expanded) => {
    const nodes = [];
    const edges = [];
    let yOffset = 0;
    const ySpacing = 80;
    const xSpacingShell = 50;
    const xSpacingSki = 250;

    // ─── By Hierarchy view ────────────────────────────────────────────────────
    // Structure: Packages/Unpackaged → Package → Commercial → [SKI Releases]/Technical
    const isByHierarchy = data.length > 0 && data[0].type === 'packages_group';
    if (isByHierarchy) {
      // Recursively compute visible row count for a node
      const getVisibleHeight = (node) => {
        if (!expanded.has(node.id) || !node.children || node.children.length === 0) return 1;
        return node.children.reduce((sum, child) => sum + getVisibleHeight(child), 0);
      };

      const totalHeight = data.reduce((sum, grp) => sum + getVisibleHeight(grp) * ySpacing, 0);
      const shellCenterY = (totalHeight - ySpacing) / 2;
      const shellNodeId = 'shell-product-hierarchy';

      nodes.push({
        id: shellNodeId,
        type: 'custom',
        data: { label: 'Product Hierarchy', type: 'shell', count: data.length, isExpanded: true },
        position: { x: xSpacingShell, y: shellCenterY },
        className: 'node-shell',
      });

      // X positions (fixed columns, always the same regardless of branch)
      const xGroups = 230;  // packages_group / unpackaged_group
      const xPkg    = 420;  // package nodes (under packages_group only)
      const xComm   = 610;  // commercial_product (always at this depth)
      const xLevel4 = 800;  // ski_releases_group / technical_product (siblings under commercial)
      const xSki    = 990;  // ski_release (under ski_releases_group)
      const xRT     = 1180; // request_type (under ski_release)

      /**
       * Render a commercial product and its children (SKI releases group + technical products).
       * commSubtreeY = the top-y of this commercial's subtree (not the center).
       */
      const renderCommSubtree = (comm, parentId, commSubtreeY) => {
        const commH = getVisibleHeight(comm);
        const commCenterY = commSubtreeY + (commH - 1) * ySpacing / 2;
        const isCommExpanded = expanded.has(comm.id);

        nodes.push({
          id: comm.id,
          type: 'custom',
          data: { label: `${comm.name}\n(${comm.count})`, type: 'commercial_product', name: comm.name, count: comm.count, isExpanded: isCommExpanded },
          position: { x: xComm, y: commCenterY },
          className: 'node-commercial-product',
        });
        edges.push({ id: `${parentId}-${comm.id}`, source: parentId, target: comm.id, type: 'default', animated: false, style: { stroke: '#666', strokeWidth: 2 } });

        if (isCommExpanded && comm.children) {
          let childY = commSubtreeY;
          comm.children.forEach((child) => {
            const childH = getVisibleHeight(child);
            const childCenterY = childY + (childH - 1) * ySpacing / 2;

            if (child.type === 'ski_releases_group') {
              const isSkiGrpExpanded = expanded.has(child.id);
              nodes.push({
                id: child.id,
                type: 'custom',
                data: { label: `${child.name}\n(${child.count})`, type: 'ski_releases_group', name: child.name, count: child.count, isExpanded: isSkiGrpExpanded },
                position: { x: xLevel4, y: childCenterY },
                className: 'node-ski-releases-group',
              });
              edges.push({ id: `${comm.id}-${child.id}`, source: comm.id, target: child.id, type: 'default', animated: false, style: { stroke: '#666', strokeWidth: 1.5 } });

              if (isSkiGrpExpanded && child.children) {
                let skiY = childY;
                child.children.forEach((ski) => {
                  const skiH = getVisibleHeight(ski);
                  const skiCenterY = skiY + (skiH - 1) * ySpacing / 2;
                  const isSkiExpanded = expanded.has(ski.id);

                  nodes.push({
                    id: ski.id,
                    type: 'custom',
                    data: { label: `${ski.name}\n(${ski.count})`, type: 'ski_release', name: ski.name, count: ski.count, fullName: ski.fullName, isExpanded: isSkiExpanded },
                    position: { x: xSki, y: skiCenterY },
                    className: 'node-ski-release',
                  });
                  edges.push({ id: `${child.id}-${ski.id}`, source: child.id, target: ski.id, type: 'default', animated: false, style: { stroke: '#666', strokeWidth: 1.5 } });

                  if (isSkiExpanded && ski.children) {
                    ski.children.forEach((rt, rtIdx) => {
                      const rtY = skiY + rtIdx * ySpacing;
                      nodes.push({
                        id: rt.id,
                        type: 'custom',
                        data: { label: rt.name, type: 'request_type', name: rt.name, count: rt.count, status: rt.status, requestIds: rt.requestIds || [], primaryRequestId: rt.primaryRequestId, isExpanded: false },
                        position: { x: xRT, y: rtY },
                        className: 'node-request-type',
                      });
                      edges.push({ id: `${ski.id}-${rt.id}`, source: ski.id, target: rt.id, type: 'default', animated: false, style: { stroke: '#666', strokeWidth: 1.5 } });
                    });
                  }
                  skiY += skiH * ySpacing;
                });
              }
            } else if (child.type === 'technical_product') {
              const isTechExpanded = expanded.has(child.id);
              nodes.push({
                id: child.id,
                type: 'custom',
                data: { label: child.count > 0 ? `${child.name}\n(${child.count})` : child.name, type: 'technical_product', name: child.name, count: child.count, isExpanded: isTechExpanded },
                position: { x: xLevel4, y: childCenterY },
                className: 'node-technical-product',
              });
              edges.push({ id: `${comm.id}-${child.id}`, source: comm.id, target: child.id, type: 'default', animated: false, style: { stroke: '#888', strokeWidth: 1, strokeDasharray: '4,3' } });

              if (isTechExpanded && child.children && child.children.length > 0) {
                const xTechSkiGrp = xLevel4 + 190;
                const xTechSki    = xTechSkiGrp + 190;
                const xTechRT     = xTechSki + 190;
                let techChildY = childY;
                child.children.forEach((techChild) => {
                  const techChildH = getVisibleHeight(techChild);
                  const techChildCenterY = techChildY + (techChildH - 1) * ySpacing / 2;
                  if (techChild.type === 'ski_releases_group') {
                    const isTechSkiGrpExpanded = expanded.has(techChild.id);
                    nodes.push({
                      id: techChild.id, type: 'custom',
                      data: { label: `${techChild.name}\n(${techChild.count})`, type: 'ski_releases_group', name: techChild.name, count: techChild.count, isExpanded: isTechSkiGrpExpanded },
                      position: { x: xTechSkiGrp, y: techChildCenterY },
                      className: 'node-ski-releases-group',
                    });
                    edges.push({ id: `${child.id}-${techChild.id}`, source: child.id, target: techChild.id, type: 'default', animated: false, style: { stroke: '#666', strokeWidth: 1.5 } });

                    if (isTechSkiGrpExpanded && techChild.children) {
                      let skiY = techChildY;
                      techChild.children.forEach((ski) => {
                        const skiH = getVisibleHeight(ski);
                        const skiCenterY = skiY + (skiH - 1) * ySpacing / 2;
                        const isSkiExpanded = expanded.has(ski.id);
                        nodes.push({
                          id: ski.id, type: 'custom',
                          data: { label: `${ski.name}\n(${ski.count})`, type: 'ski_release', name: ski.name, count: ski.count, fullName: ski.fullName, isExpanded: isSkiExpanded },
                          position: { x: xTechSki, y: skiCenterY },
                          className: 'node-ski-release',
                        });
                        edges.push({ id: `${techChild.id}-${ski.id}`, source: techChild.id, target: ski.id, type: 'default', animated: false, style: { stroke: '#666', strokeWidth: 1.5 } });

                        if (isSkiExpanded && ski.children) {
                          ski.children.forEach((rt, rtIdx) => {
                            const rtY = skiY + rtIdx * ySpacing;
                            nodes.push({
                              id: rt.id, type: 'custom',
                              data: { label: rt.name, type: 'request_type', name: rt.name, count: rt.count, status: rt.status, requestIds: rt.requestIds || [], primaryRequestId: rt.primaryRequestId, isExpanded: false },
                              position: { x: xTechRT, y: rtY },
                              className: 'node-request-type',
                            });
                            edges.push({ id: `${ski.id}-${rt.id}`, source: ski.id, target: rt.id, type: 'default', animated: false, style: { stroke: '#666', strokeWidth: 1.5 } });
                          });
                        }
                        skiY += skiH * ySpacing;
                      });
                    }
                  }
                  techChildY += techChildH * ySpacing;
                });
              }
            }

            childY += childH * ySpacing;
          });
        }
      };

      // Render top-level grouping nodes (packages_group, unpackaged_group)
      data.forEach((topGroup) => {
        const topGroupH = getVisibleHeight(topGroup);
        const topGroupCenterY = yOffset + (topGroupH - 1) * ySpacing / 2;
        const isTopExpanded = expanded.has(topGroup.id);

        nodes.push({
          id: topGroup.id,
          type: 'custom',
          data: { label: `${topGroup.name}\n(${topGroup.count})`, type: topGroup.type, name: topGroup.name, count: topGroup.count, isExpanded: isTopExpanded },
          position: { x: xGroups, y: topGroupCenterY },
          className: `node-${topGroup.type.replace('_', '-')}`,
        });
        edges.push({ id: `${shellNodeId}-${topGroup.id}`, source: shellNodeId, target: topGroup.id, type: 'default', animated: false, style: { stroke: '#333', strokeWidth: 3 } });

        if (isTopExpanded && topGroup.children) {
          let childYOffset = yOffset;

          if (topGroup.type === 'packages_group') {
            // Children are packages → each package has commercial children
            topGroup.children.forEach((pkg) => {
              const pkgH = getVisibleHeight(pkg);
              const pkgCenterY = childYOffset + (pkgH - 1) * ySpacing / 2;
              const isPkgExpanded = expanded.has(pkg.id);

              nodes.push({
                id: pkg.id,
                type: 'custom',
                data: { label: `${pkg.name}\n(${pkg.count})`, type: 'package', name: pkg.name, count: pkg.count, isExpanded: isPkgExpanded },
                position: { x: xPkg, y: pkgCenterY },
                className: 'node-package',
              });
              edges.push({ id: `${topGroup.id}-${pkg.id}`, source: topGroup.id, target: pkg.id, type: 'default', animated: false, style: { stroke: '#666', strokeWidth: 2 } });

              if (isPkgExpanded && pkg.children) {
                let commYOffset = childYOffset;
                pkg.children.forEach((comm) => {
                  renderCommSubtree(comm, pkg.id, commYOffset);
                  commYOffset += getVisibleHeight(comm) * ySpacing;
                });
              }
              childYOffset += pkgH * ySpacing;
            });
          } else {
            // unpackaged_group: children are commercial products directly
            topGroup.children.forEach((comm) => {
              renderCommSubtree(comm, topGroup.id, childYOffset);
              childYOffset += getVisibleHeight(comm) * ySpacing;
            });
          }
        }
        yOffset += topGroupH * ySpacing;
      });

      return { nodes, edges };
    }
    // ─── End By Hierarchy view ────────────────────────────────────────────────

    // X positions for standard views (By SKI Release / By Product)
    const xSpacingRequestType = 450;
    const xSpacingGprSubtype = 650;
    const xSpacingProduct = 650;
    const xSpacingProductUnderGpr = 850;

    // X positions for By Release Type view (extra level shifts everything right by 200)
    const xSpacingReleaseType = 450;
    const xSpacingRTForRelease = 650;
    const xSpacingGprForRelease = 850;
    const xSpacingProductForRelease = 850;
    const xSpacingProductUnderGprForRelease = 1050;

    // Calculate total height for shell node centering
    const totalSkiReleases = data.length;
    let totalHeight = 0;
    data.forEach((skiRelease) => {
      const isSkiExpanded = expanded.has(skiRelease.id);
      const numChildren = isSkiExpanded && skiRelease.children ? skiRelease.children.length : 1;
      totalHeight += (numChildren - 1) * ySpacing + ySpacing;
    });

    // Shell node "Launches" at the left center
    const shellNodeId = 'shell-ski-releases';
    const shellCenterY = (totalHeight - ySpacing) / 2;

    nodes.push({
      id: shellNodeId,
      type: 'custom',
      data: {
        label: 'Launches',
        type: 'shell',
        count: totalSkiReleases,
        isExpanded: true,
      },
      position: { x: xSpacingShell, y: shellCenterY },
      className: 'node-shell',
    });

    data.forEach((skiRelease) => {
      const skiReleaseId = skiRelease.id;
      const isSkiExpanded = expanded.has(skiReleaseId);

      const numChildren = isSkiExpanded && skiRelease.children ? skiRelease.children.length : 1;
      const childrenHeight = (numChildren - 1) * ySpacing;
      const skiCenterY = yOffset + childrenHeight / 2;

      nodes.push({
        id: skiReleaseId,
        type: 'custom',
        data: {
          label: `${skiRelease.name}\n(${skiRelease.count})`,
          type: skiRelease.type,
          count: skiRelease.count,
          fullName: skiRelease.fullName,
          productType: skiRelease.productType,
          isExpanded: isSkiExpanded,
        },
        position: { x: xSpacingSki, y: skiCenterY },
        className: `node-${skiRelease.type}`,
      });

      edges.push({
        id: `${shellNodeId}-${skiReleaseId}`,
        source: shellNodeId,
        target: skiReleaseId,
        type: 'default',
        animated: false,
        style: { stroke: '#333', strokeWidth: 3 },
      });

      if (isSkiExpanded && skiRelease.children) {
        // Detect By Release Type view — children are release_type nodes
        const hasReleaseTypeChildren =
          skiRelease.children.length > 0 && skiRelease.children[0].type === 'release_type';

        if (hasReleaseTypeChildren) {
          // ─── By Release Type view ────────────────────────────────────────
          skiRelease.children.forEach((releaseType, rtIdx) => {
            const releaseTypeId = releaseType.id;
            const isReleaseTypeExpanded = expanded.has(releaseTypeId);
            const releaseTypeY = yOffset + rtIdx * ySpacing;

            nodes.push({
              id: releaseTypeId,
              type: 'custom',
              data: {
                label: `${releaseType.name}\n(${releaseType.count})`,
                type: 'release_type',
                name: releaseType.name,
                count: releaseType.count,
                isExpanded: isReleaseTypeExpanded,
              },
              position: { x: xSpacingReleaseType, y: releaseTypeY },
              className: 'node-release-type',
            });

            edges.push({
              id: `${skiReleaseId}-${releaseTypeId}`,
              source: skiReleaseId,
              target: releaseTypeId,
              type: 'default',
              animated: false,
              style: { stroke: '#666', strokeWidth: 2 },
            });

            if (isReleaseTypeExpanded && releaseType.children) {
              releaseType.children.forEach((requestType, reqIdx) => {
                const requestTypeId = requestType.id;
                const isReqExpanded = expanded.has(requestTypeId);
                const reqY = releaseTypeY + reqIdx * ySpacing;

                nodes.push({
                  id: requestTypeId,
                  type: 'custom',
                  data: {
                    label: `${requestType.name}\n(${requestType.count})`,
                    type: 'request_type',
                    name: requestType.name,
                    count: requestType.count,
                    isExpanded: isReqExpanded,
                    requestIds: requestType.requestIds || [],
                    primaryRequestId: requestType.primaryRequestId,
                  },
                  position: { x: xSpacingRTForRelease, y: reqY },
                  className: 'node-request-type',
                });

                edges.push({
                  id: `${releaseTypeId}-${requestTypeId}`,
                  source: releaseTypeId,
                  target: requestTypeId,
                  type: 'default',
                  animated: false,
                  style: { stroke: '#666', strokeWidth: 1.5 },
                });

                if (isReqExpanded && requestType.children) {
                  requestType.children.forEach((child, childIdx) => {
                    const childId = child.id;

                    if (child.type === 'gpr_subtype') {
                      const isGprExpanded = expanded.has(childId);
                      const gprY = reqY + childIdx * ySpacing;

                      nodes.push({
                        id: childId,
                        type: 'custom',
                        data: {
                          label: `${child.name}\n(${child.count})`,
                          type: 'gpr_subtype',
                          name: child.name,
                          count: child.count,
                          isExpanded: isGprExpanded,
                        },
                        position: { x: xSpacingGprForRelease, y: gprY },
                        className: 'node-request-type',
                      });

                      edges.push({
                        id: `${requestTypeId}-${childId}`,
                        source: requestTypeId,
                        target: childId,
                        type: 'default',
                        animated: false,
                        style: { stroke: '#666', strokeWidth: 1.5 },
                      });

                      if (isGprExpanded && child.children) {
                        child.children.forEach((product, prodIdx) => {
                          const productId = product.id;
                          const prodY = gprY + prodIdx * 40;
                          const label = product.status
                            ? `${product.name}\n(${product.status})`
                            : `${product.name}\n(${product.count})`;

                          nodes.push({
                            id: productId,
                            type: 'custom',
                            data: {
                              label,
                              type: 'product',
                              name: product.name,
                              count: product.count,
                              status: product.status,
                              productType: product.productType,
                              requestIds: product.requestIds || [],
                              primaryRequestId: product.primaryRequestId,
                            },
                            position: { x: xSpacingProductUnderGprForRelease, y: prodY },
                            className: 'node-product',
                          });

                          edges.push({
                            id: `${childId}-${productId}`,
                            source: childId,
                            target: productId,
                            type: 'default',
                            animated: false,
                            style: { stroke: '#666', strokeWidth: 1.5 },
                          });
                        });
                      }
                    } else {
                      // Regular product node under request_type
                      const prodY = reqY + childIdx * ySpacing;
                      const label = child.status
                        ? `${child.name}\n(${child.status})`
                        : `${child.name}\n(${child.count})`;

                      nodes.push({
                        id: child.id,
                        type: 'custom',
                        data: {
                          label,
                          type: 'product',
                          name: child.name,
                          count: child.count,
                          status: child.status,
                          productType: child.productType,
                          requestIds: child.requestIds || [],
                          primaryRequestId: child.primaryRequestId,
                        },
                        position: { x: xSpacingProductForRelease, y: prodY },
                        className: 'node-product',
                      });

                      edges.push({
                        id: `${requestTypeId}-${child.id}`,
                        source: requestTypeId,
                        target: child.id,
                        type: 'default',
                        animated: false,
                        style: { stroke: '#666', strokeWidth: 1.5 },
                      });
                    }
                  });
                }
              });
            }
          });
        } else {
          // ─── By SKI Release / By Product view (existing logic) ───────────
          skiRelease.children.forEach((requestType, rtIndex) => {
            const requestTypeId = requestType.id;
            const isRequestTypeExpanded = expanded.has(requestTypeId);
            const requestTypeYOffset = yOffset + rtIndex * ySpacing;

            // Build label with change indicator and breaking change indicator if available
            let requestTypeLabel = `${requestType.name}\n(${requestType.count})`;
            if (requestType.status && requestType.changeIndicator) {
              const changeIndicator = requestType.changeIndicator || '';
              const breakingIndicator = requestType.breakingChangeIndicator || '';
              const indicators = `${changeIndicator}${breakingIndicator ? ' ' + breakingIndicator : ''}`;
              requestTypeLabel = `${indicators} ${requestType.name}\n(${requestType.status})`;
            }

            nodes.push({
              id: requestTypeId,
              type: 'custom',
              data: {
                label: requestTypeLabel,
                type: 'request_type',
                name: requestType.name,
                count: requestType.count,
                isExpanded: isRequestTypeExpanded,
                requestIds: requestType.requestIds || [],
                primaryRequestId: requestType.primaryRequestId,
              },
              position: { x: xSpacingRequestType, y: requestTypeYOffset },
              className: 'node-request-type',
            });

            edges.push({
              id: `${skiReleaseId}-${requestTypeId}`,
              source: skiReleaseId,
              target: requestTypeId,
              type: 'default',
              animated: false,
              style: { stroke: '#666', strokeWidth: 2 },
            });

            if (isRequestTypeExpanded && requestType.children) {
              requestType.children.forEach((child, childIndex) => {
                const childId = child.id;

                if (child.type === 'gpr_subtype') {
                  // GPR sub-type intermediate node
                  const isGprSubtypeExpanded = expanded.has(childId);
                  const gprSubtypeYOffset = requestTypeYOffset + childIndex * ySpacing;

                  nodes.push({
                    id: childId,
                    type: 'custom',
                    data: {
                      label: `${child.name}\n(${child.count})`,
                      type: 'gpr_subtype',
                      name: child.name,
                      count: child.count,
                      isExpanded: isGprSubtypeExpanded,
                    },
                    position: { x: xSpacingGprSubtype, y: gprSubtypeYOffset },
                    className: 'node-request-type',
                  });

                  edges.push({
                    id: `${requestTypeId}-${childId}`,
                    source: requestTypeId,
                    target: childId,
                    type: 'default',
                    animated: false,
                    style: { stroke: '#666', strokeWidth: 1.5 },
                  });

                  // Render products under this GPR sub-type
                  if (isGprSubtypeExpanded && child.children) {
                    child.children.forEach((product, productIndex) => {
                      const productId = product.id;
                      const productYOffset = gprSubtypeYOffset + productIndex * 40;

                      let label = `${product.name}\n(${product.count})`;
                      if (product.status) {
                        const changeIndicator = product.changeIndicator || '';
                        const breakingIndicator = product.breakingChangeIndicator || '';
                        const indicators = `${changeIndicator}${breakingIndicator ? ' ' + breakingIndicator : ''}`;
                        label = `${indicators} ${product.name}\n(${product.status})`;
                      }

                      nodes.push({
                        id: productId,
                        type: 'custom',
                        data: {
                          label,
                          type: 'product',
                          name: product.name,
                          count: product.count,
                          status: product.status,
                          productType: product.productType,
                          requestIds: product.requestIds || [],
                          primaryRequestId: product.primaryRequestId,
                        },
                        position: { x: xSpacingProductUnderGpr, y: productYOffset },
                        className: 'node-product',
                      });

                      edges.push({
                        id: `${childId}-${productId}`,
                        source: childId,
                        target: productId,
                        type: 'default',
                        animated: false,
                        style: { stroke: '#666', strokeWidth: 1.5 },
                      });
                    });
                  }
                } else if (child.type === 'request_type' && child.children && child.children.length > 0) {
                  // GPR intermediate request_type node in "By Product" view — expandable
                  const isGprNodeExpanded = expanded.has(childId);
                  const gprNodeYOffset = requestTypeYOffset + childIndex * ySpacing;

                  nodes.push({
                    id: childId,
                    type: 'custom',
                    data: {
                      label: `${child.name}\n(${child.count})`,
                      type: 'request_type',
                      name: child.name,
                      count: child.count,
                      isExpanded: isGprNodeExpanded,
                      requestIds: child.requestIds || [],
                      primaryRequestId: child.primaryRequestId,
                    },
                    position: { x: xSpacingProduct, y: gprNodeYOffset },
                    className: 'node-request-type',
                  });

                  edges.push({
                    id: `${requestTypeId}-${childId}`,
                    source: requestTypeId,
                    target: childId,
                    type: 'default',
                    animated: false,
                    style: { stroke: '#666', strokeWidth: 1.5 },
                  });

                  // Render gpr_subtype leaf nodes when GPR is expanded
                  if (isGprNodeExpanded && child.children) {
                    child.children.forEach((subtypeNode, subtypeIndex) => {
                      const subtypeId = subtypeNode.id;
                      const subtypeYOffset = gprNodeYOffset + subtypeIndex * 40;

                      nodes.push({
                        id: subtypeId,
                        type: 'custom',
                        data: {
                          label: `${subtypeNode.name}\n(${subtypeNode.count})`,
                          type: 'gpr_subtype',
                          name: subtypeNode.name,
                          count: subtypeNode.count,
                          status: subtypeNode.status,
                          requestIds: subtypeNode.requestIds || [],
                          primaryRequestId: subtypeNode.primaryRequestId,
                        },
                        position: { x: xSpacingProductUnderGpr, y: subtypeYOffset },
                        className: 'node-request-type',
                      });

                      edges.push({
                        id: `${childId}-${subtypeId}`,
                        source: childId,
                        target: subtypeId,
                        type: 'default',
                        animated: false,
                        style: { stroke: '#666', strokeWidth: 1.5 },
                      });
                    });
                  }
                } else {
                  // Regular leaf node (product in "By SKI Release", non-GPR request_type in "By Product")
                  const productId = childId;
                  const productYOffset = requestTypeYOffset + childIndex * ySpacing;

                  let label = `${child.name}\n(${child.count})`;
                  if (child.status) {
                    const changeIndicator = child.changeIndicator || '';
                    const breakingIndicator = child.breakingChangeIndicator || '';
                    const indicators = `${changeIndicator}${breakingIndicator ? ' ' + breakingIndicator : ''}`;
                    label = `${indicators} ${child.name}\n(${child.status})`;
                  }

                  nodes.push({
                    id: productId,
                    type: 'custom',
                    data: {
                      label,
                      type: 'product',
                      name: child.name,
                      count: child.count,
                      status: child.status,
                      productType: child.productType,
                      requestIds: child.requestIds || [],
                      primaryRequestId: child.primaryRequestId,
                    },
                    position: { x: xSpacingProduct, y: productYOffset },
                    className: 'node-product',
                  });

                  edges.push({
                    id: `${requestTypeId}-${productId}`,
                    source: requestTypeId,
                    target: productId,
                    type: 'default',
                    animated: false,
                    style: { stroke: '#666', strokeWidth: 1.5 },
                  });
                }
              });
            }
          });
        }

        // Update yOffset for next SKI Release
        yOffset += (skiRelease.children.length + 1) * ySpacing;
      } else {
        yOffset += ySpacing;
      }
    });

    return { nodes, edges };
  }, []);

  /**
   * Handle node click to expand/collapse and navigate for CuC requests
   */
  const handleNodeClick = useCallback((event, node) => {
    const nodeId = node.id;
    const nodeType = node.data.type;
    const nodeData = node.data;

    // Handle Product node clicks — navigate to request view for any request type
    if (nodeType === 'product') {
      const requestIds = nodeData.requestIds || [];
      const primaryRequestId = nodeData.primaryRequestId;

      if (requestIds.length === 1 || (requestIds.length === 0 && primaryRequestId)) {
        history.push(`/request/${requestIds[0] || primaryRequestId}`, { from: 'reporting' });
        return;
      } else if (requestIds.length > 1) {
        setMultiRequestModal({ show: true, productName: nodeData.name, requestIds });
        return;
      }

      // No request IDs — expand/collapse
      setExpandedNodes((prev) => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(nodeId)) newExpanded.delete(nodeId);
        else newExpanded.add(nodeId);
        return newExpanded;
      });
      return;
    }

    // Handle GPR sub-type node clicks
    else if (nodeType === 'gpr_subtype') {
      const requestIds = nodeData.requestIds || [];
      const primaryRequestId = nodeData.primaryRequestId;

      if (requestIds.length === 1 || (requestIds.length === 0 && primaryRequestId)) {
        history.push(`/request/${requestIds[0] || primaryRequestId}`, { from: 'reporting' });
        return;
      } else if (requestIds.length > 1) {
        setMultiRequestModal({ show: true, productName: nodeData.name, requestIds });
        return;
      }

      setExpandedNodes((prev) => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(nodeId)) newExpanded.delete(nodeId);
        else newExpanded.add(nodeId);
        return newExpanded;
      });
    }

    // Handle Request Type node clicks
    else if (nodeType === 'request_type') {
      const requestTypeName = extractRequestTypeFromId(nodeId, nodeData);

      if (isCuCRequestType(requestTypeName)) {
        const requestIds = nodeData.requestIds || [];
        const primaryRequestId = nodeData.primaryRequestId;

        if (requestIds.length === 1 || (requestIds.length === 0 && primaryRequestId)) {
          history.push(`/request/${requestIds[0] || primaryRequestId}`, { from: 'reporting' });
          return;
        } else if (requestIds.length > 1) {
          setMultiRequestModal({ show: true, productName: nodeData.name, requestIds });
          return;
        }
      }

      setExpandedNodes((prev) => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(nodeId)) newExpanded.delete(nodeId);
        else newExpanded.add(nodeId);
        return newExpanded;
      });
    }

    // Handle SKI Release, Release Type, and hierarchy node clicks — expand/collapse
    else if (nodeType === 'ski_release' || nodeType === 'release_type' || nodeType === 'package' || nodeType === 'commercial_product' || nodeType === 'technical_product' || nodeType === 'packages_group' || nodeType === 'unpackaged_group' || nodeType === 'ski_releases_group') {
      setExpandedNodes((prev) => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(nodeId)) newExpanded.delete(nodeId);
        else newExpanded.add(nodeId);
        return newExpanded;
      });
    }

    // Call parent handler (except for product nodes and request_type nodes which return early above)
    if (onNodeClick) {
      onNodeClick(node);
    }
  }, [onNodeClick, history]);

  /**
   * Update nodes and edges when data or expanded state changes
   */
  useEffect(() => {
    if (hierarchyData && hierarchyData.length > 0) {
      const { nodes: graphNodes, edges: graphEdges } = transformDataToGraph(
        hierarchyData,
        expandedNodes
      );
      setNodes(graphNodes);
      setEdges(graphEdges);
    }
  }, [hierarchyData, expandedNodes, transformDataToGraph, setNodes, setEdges]);

  /**
   * Custom node component - Circle on left, text centered to the right
   */
  const nodeTypes = useMemo(() => {
    // Helper function to render label with tooltips for indicators
    const renderLabelWithTooltips = (label) => {
      if (!label) return null;

      // Map of indicators to their tooltips
      const indicatorTooltips = {
        '✓': 'Changed',
        '✗': 'No Change',
        '🕐': 'New Product',
        '❓': 'No Prior Release',
        '💥': 'Breaking Change',
      };

      const indicators = [];
      let remainingLabel = label;

      for (const [symbol, tooltip] of Object.entries(indicatorTooltips)) {
        if (remainingLabel.includes(symbol)) {
          indicators.push({ symbol, tooltip });
          remainingLabel = remainingLabel.replace(symbol, '').trim();
        }
      }

      if (indicators.length === 0) {
        return <span>{label}</span>;
      }

      return (
        <span>
          {indicators.map((indicator, index) => (
            <span
              key={index}
              title={indicator.tooltip}
              className="indicator-tooltip"
              style={{ cursor: 'help', marginRight: '4px' }}
            >
              {indicator.symbol}
            </span>
          ))}
          <span>{remainingLabel}</span>
        </span>
      );
    };

    return {
      custom: ({ data }) => {
        const isExpandable =
          data.type === 'ski_release' ||
          data.type === 'release_type' ||
          data.type === 'request_type' ||
          data.type === 'product' ||
          data.type === 'package' ||
          data.type === 'commercial_product' ||
          data.type === 'technical_product' ||
          data.type === 'packages_group' ||
          data.type === 'unpackaged_group' ||
          data.type === 'ski_releases_group';
        const showIndicator = isExpandable && data.count > 0;

        return (
          <div className={`custom-node custom-node--${data.type}`}>
            {/* Input handle for incoming edges from left */}
            <Handle
              type="target"
              position={Position.Left}
              style={{ opacity: 0 }}
            />

            <div className="custom-node__content">
              <div className="custom-node__circle">
                {showIndicator && (
                  <div className="custom-node__indicator">
                    {data.isExpanded ? '−' : '+'}
                  </div>
                )}
              </div>
              <div className="custom-node__label">
                {data.type === 'product' && data.productType && (
                  <ProductTypeIcon productType={data.productType} size={13} />
                )}
                {renderLabelWithTooltips(data.label)}
              </div>
            </div>

            {/* Output handle for outgoing edges to right */}
            <Handle
              type="source"
              position={Position.Right}
              style={{ opacity: 0 }}
            />
          </div>
        );
      },
    };
  }, []);

  if (!hierarchyData || hierarchyData.length === 0) {
    return (
      <div className="network-graph__empty">
        <p>No data available to display</p>
      </div>
    );
  }

  return (
    <div className="network-graph">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        fitView
        fitViewOptions={{
          padding: 0.3,
          includeHiddenNodes: false,
          minZoom: 0.5,
          maxZoom: 1
        }}
        attributionPosition="bottom-left"
        minZoom={0.3}
        maxZoom={2}
        defaultViewport={{ x: 200, y: 50, zoom: 0.9 }}
      >
        <Background color="#e5e5e5" gap={16} />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={(node) => {
            switch (node.data.type) {
              case 'shell':              return '#9ca3af';
              case 'ski_release':        return '#5b9bd5';
              case 'release_type':       return '#5eadd4';
              case 'product':            return '#70ad47';
              case 'request_type':       return '#ffc000';
              case 'packages_group':      return '#5b21b6';
              case 'unpackaged_group':   return '#475569';
              case 'package':            return '#7c3aed';
              case 'commercial_product': return '#0d9488';
              case 'technical_product':  return '#b45309';
              case 'ski_releases_group': return '#0891b2';
              default:                   return '#999';
            }
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
      </ReactFlow>

      {/* Multi-Request Selection Modal */}
      {multiRequestModal.show && (
        <div className="modal-overlay" onClick={() => setMultiRequestModal({ show: false, productName: '', requestIds: [] })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select a Request</h3>
              <button
                className="modal-close"
                onClick={() => setMultiRequestModal({ show: false, productName: '', requestIds: [] })}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Multiple requests found for <strong>{multiRequestModal.productName}</strong>. Please select one:</p>
              <div className="request-list">
                {multiRequestModal.requestIds.map((requestId) => (
                  <button
                    key={requestId}
                    className="request-list-item"
                    onClick={() => {
                      history.push(`/request/${requestId}`, { from: 'reporting' });
                      setMultiRequestModal({ show: false, productName: '', requestIds: [] });
                    }}
                  >
                    {requestId}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

NetworkGraph.propTypes = {
  hierarchyData: PropTypes.array.isRequired,
  onNodeClick: PropTypes.func,
  expandAllTrigger: PropTypes.number,
};

NetworkGraph.defaultProps = {
  onNodeClick: null,
};

export default NetworkGraph;
